// @greenlight-spec: 16-api-conventions.md

import * as React from 'react';

/**
 * The Prev/Next cursor trail for a paged view (`/apps`, `/marketplace`),
 * persisted to sessionStorage so it survives a refresh: refreshing page 3
 * leaves Previous pointing at page 2, not at nothing. Session-scoped on
 * purpose — a shared link opened in a new tab has no trail (that visitor
 * never saw the earlier pages), and its Previous falls back to page one.
 */
export function useSessionCursorStack(scopeKey: string): {
  stack: readonly string[];
  push: (cursor: string) => void;
  /** Removes and returns the top cursor; undefined when the trail is empty. */
  pop: () => string | undefined;
  clear: () => void;
} {
  const storageKey = `gl-cursor-trail:${scopeKey}`;
  const [stack, setStack] = React.useState<string[]>(() => readTrail(storageKey));

  const write = React.useCallback(
    (next: string[]) => {
      setStack(next);
      try {
        if (next.length === 0) window.sessionStorage.removeItem(storageKey);
        else window.sessionStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // Storage unavailable (quota, privacy mode): in-memory behavior only.
      }
    },
    [storageKey],
  );

  // useState initializers don't re-run on re-render, so a scopeKey change
  // (one component instance reused across scopes) re-reads the new trail.
  const lastKeyRef = React.useRef(storageKey);
  React.useEffect(() => {
    if (lastKeyRef.current === storageKey) return;
    lastKeyRef.current = storageKey;
    setStack(readTrail(storageKey));
  }, [storageKey]);

  const stackRef = React.useRef(stack);
  stackRef.current = stack;

  const push = React.useCallback((cursor: string) => write([...stackRef.current, cursor]), [write]);
  const pop = React.useCallback((): string | undefined => {
    const current = stackRef.current;
    if (current.length === 0) return undefined;
    const top = current[current.length - 1];
    write(current.slice(0, -1));
    return top;
  }, [write]);
  const clear = React.useCallback(() => {
    if (stackRef.current.length > 0) write([]);
  }, [write]);

  return { stack, push, pop, clear };
}

function readTrail(storageKey: string): string[] {
  try {
    const raw = window.sessionStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((c): c is string => typeof c === 'string') : [];
  } catch {
    return [];
  }
}
